/**
 * Utility functions for the Scans V2 page.
 * Status color helpers, formatters, and shared constants.
 */

import moment from "moment-timezone";
import type { ScanStatus, ScheduleStatus, StatusStyle } from "./scansV2.types";

// ──────────────────────────────────────────────
// Scan Status Styles
// ──────────────────────────────────────────────

export const getScanStatusStyle = (status: ScanStatus): StatusStyle => {
  switch (status) {
    case "Completed":
    case "Success":
    case "Fetching Configuration Success":
      return {
        color: "#00a61b",
        background: "rgba(0, 166, 27, 0.06)",
        border: "1.5px solid #00a61b",
        dotColor: "#00a61b",
      };
    case "Fetching Configuration Failed":
    case "Failed":
      return {
        color: "#f53b3b",
        background: "rgba(245, 59, 59, 0.06)",
        border: "1.5px solid #f53b3b",
        dotColor: "#f53b3b",
      };
    case "Partially Success":
      return {
        color: "#F7941F",
        background: "rgba(247, 148, 31, 0.06)",
        border: "1.5px solid #F7941F",
        dotColor: "#F7941F",
      };
    case "Cancelled":
      return {
        color: "#696595",
        background: "rgba(105, 101, 149, 0.06)",
        border: "1.5px solid #696595",
        dotColor: "#696595",
      };
    case "Queue":
    case "Processing":
    case "Fetching Configuration":
    case "In Progress":
      return {
        color: "#3A2ADA",
        background: "rgba(58, 42, 218, 0.06)",
        border: "1.5px solid #3A2ADA",
        dotColor: "#3A2ADA",
      };
    case "Paused":
      return {
        color: "#EAB308",
        background: "#FEFCE8",
        border: "1.5px solid #EAB308",
        dotColor: "#EAB308",
      };
    default:
      return {
        color: "#9E9E9E",
        background: "rgba(158, 158, 158, 0.06)",
        border: "1.5px solid #9E9E9E",
        dotColor: "#9E9E9E",
      };
  }
};

// ──────────────────────────────────────────────
// Schedule Status Styles
// ──────────────────────────────────────────────

export const getScheduleStatusStyle = (
  status: ScheduleStatus
): { color: string; background: string; border: string } => {
  switch (status) {
    case "Active":
      return {
        color: "#00a61b",
        background: "rgba(0, 166, 27, 0.08)",
        border: "1.5px solid #00a61b",
      };
    case "Completed":
      return {
        color: "#696595",
        background: "rgba(105, 101, 149, 0.08)",
        border: "1.5px solid #696595",
      };
    case "Cancelled":
      return {
        color: "#f53b3b",
        background: "rgba(245, 59, 59, 0.08)",
        border: "1.5px solid #f53b3b",
      };
    case "Paused":
      return {
        color: "#EAB308",
        background: "#FEFCE8",
        border: "1.5px solid #EAB308",
      };
    case "Inactive":
      return {
        color: "#9E9E9E",
        background: "rgba(158, 158, 158, 0.08)",
        border: "1.5px solid #9E9E9E",
      };
    default:
      return {
        color: "#9E9E9E",
        background: "rgba(158, 158, 158, 0.08)",
        border: "1.5px solid #9E9E9E",
      };
  }
};

// ──────────────────────────────────────────────
// Date / Time Formatters
// ──────────────────────────────────────────────

/**
 * Safely parses heterogeneous date strings (ISO format with T/Z, or space-separated e.g. "2026-07-23 10:12:15 IST")
 * into a numeric epoch millisecond timestamp for consistent sorting and comparison.
 */
export const parseFlexibleDate = (dateStr: any): number => {
  if (!dateStr) return NaN;
  if (typeof dateStr === "number") return dateStr;
  const str = String(dateStr).trim();
  if (!str) return NaN;

  if (str.includes("T")) {
    const t = new Date(str).getTime();
    if (!Number.isNaN(t)) return t;
  }

  // Handle space-separated date strings like "2026-07-23 10:12:15 IST" or "2026-07-23 10:12:15"
  // Strip trailing timezone abbreviation if present (e.g. IST, UTC, EST, GMT)
  const cleaned = str.replace(/\s+[A-Z]{2,5}$/i, "").trim();

  // Convert "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ" (assume time digits are already UTC)
  const isoLike = cleaned.replace(" ", "T") + (cleaned.includes("Z") ? "" : "Z");
  const parsedIso = new Date(isoLike).getTime();
  if (!Number.isNaN(parsedIso)) return parsedIso;

  // Fallback to standard Date parsing
  return new Date(str).getTime();
};


/**
 * Formats an ISO date string to "DD MMM, YYYY - HH:mm:ss"
 * e.g. "10 Jan, 2023 - 12:12:08"
 */
export const formatScanTime = (isoDate: string): string => {
  if (!isoDate) return "-";
  
  let cleanedDate = isoDate;
  if (typeof isoDate === 'string') {
    // Remove " IST" suffix often sent by websocket payloads
    cleanedDate = isoDate.replace(/\s+IST$/, '');
  }

  // Use strict parsing with multiple acceptable formats to avoid JS Date fallback warnings
  // and handle both standard ISO strings and custom backend strings
  const formats = [
    moment.ISO_8601,
    "YYYY-MM-DD HH:mm:ss",
    "YYYY/MM/DD HH:mm:ss",
    "DD MMM, YYYY - hh:mm:ss A",
    "DD MMM, YYYY - HH:mm:ss"
  ];
  
  const m = moment.tz(cleanedDate, formats, "Asia/Kolkata");
  
  if (m.isValid()) {
    return m.format("DD MMM, YYYY - hh:mm:ss A");
  }
  
  // If parsing completely fails, return original string rather than "Invalid date"
  return isoDate;
};

/**
 * Maps internal scan type strings to user-friendly labels.
 * Replaces underscores with spaces and returns the type as-is from API.
 * e.g. "onprem_discovery_posture" -> "onprem discovery posture"
 */
export const formatScanType = (type: string): string => {
  if (!type) return "-";
  // Replace underscores with spaces
  return type.replace(/_/g, " ");
};
